import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Link from 'components/atoms/Link/Link';
import Card from "@mui/material/Card";

import MDBox from "components/atoms/MDBox/MDBox";
import MDTypography from "components/atoms/MDTypography/MDTypography";
import MDButton, { MDButtonVariants } from "components/atoms/MDButton/MDButton";
import MDInput from "components/atoms/MDInput/MDInput";

import BasicLayout from "components/templates/BasicLayout/BasicLayout";

import UserPool from 'services/cognitoUserPoolService';
import { CognitoUser } from 'amazon-cognito-identity-js';

// Images
import bgImage from 'assets/images/stock/stretching.png';
import Logo from '../../../assets/images/logo/JointAi2025Logo.png';

// Styles
import { MUIBaseColors, MUIBaseVariants, MUIColors } from 'models/StyleModels';
import { routesMap } from 'data/routes';
import { RouteKeys } from 'models/RouteModels';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetRequested, setResetRequested] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const requestPasswordReset = (event: React.FormEvent) => {
    event.preventDefault();

    if (!email) {
      setValidationErrors('Please enter your email');
      return;
    }

    setRequestLoading(true);
    setValidationErrors('');

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: UserPool
    });


    cognitoUser.forgotPassword({
      onSuccess: () => {
        setResetRequested(true);
        setRequestLoading(false);
        setSuccessMessage('Verification code sent to your email');
      },
      onFailure: (err) => {
        setRequestLoading(false);
        setValidationErrors(err.message || 'Something went wrong. Please try again.');
      }
    });
  };

  const submitNewPassword = (event: React.FormEvent) => {
    event.preventDefault();

    if (!verificationCode || !newPassword) {
      setValidationErrors('Please enter verification code and new password');
      return;
    }

    if (newPassword !== confirmPassword) {
      setValidationErrors('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setValidationErrors('Password must be at least 8 characters long');
      return;
    }

    setRequestLoading(true);
    setValidationErrors('');

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: UserPool
    });

    cognitoUser.confirmPassword(verificationCode, newPassword, {
      onSuccess: () => {
        setRequestLoading(false);
        setSuccessMessage('Password reset successful');
        setTimeout(() => {
          navigate(routesMap[RouteKeys.SIGN_IN].route);
        }, 2000);
      },
      onFailure: (err) => {
        setRequestLoading(false);
        setValidationErrors(err.message || 'Something went wrong. Please try again.');
      }
    });
  };

  return (
    <BasicLayout image={bgImage}>
      <Card
        className="sign-in"
        style={{
          backgroundColor: '#22459fcc',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: "translate(-50%, -50%)", minWidth: '350px', maxWidth: '425px', overflowY: 'auto', maxHeight: '85vh',
        }}
      >

        <MDBox
          style={{
            width: '100%',
            opacity: 1,
            background: '#ffffff',
            borderRadius: '0.5rem',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          variant='gradient'
          bgColor={MUIColors.WHITE}
          borderRadius='lg'
          coloredShadow='light'
          my={2}
          p={3}
          textAlign='center'
        >
          <img src={Logo}
            style={{
              width: '100%',
              height: 'auto',
              objectFit: 'contain'
            }}
            alt="logo" />
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          {validationErrors && (
            <MDBox mb={2}>
              <MDTypography variant="body2" color={MUIColors.ERROR} fontWeight="medium" textAlign="center" display="block">
                {validationErrors}
              </MDTypography>
            </MDBox>
          )}

          {successMessage && (
            <MDBox mb={2}>
              <MDTypography variant="body2" color={MUIColors.SUCCESS} fontWeight="medium" textAlign="center" display="block">
                {successMessage}
              </MDTypography>
            </MDBox>
          )}

          {!resetRequested ? (
            <MDBox component="form" role="form" onSubmit={requestPasswordReset}>
              <MDBox mb={2}>
                <MDInput
                  type="email"
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  required
                />
              </MDBox>

              <MDBox mt={4} mb={1}>
                <MDButton
                  type='submit'
                  variant={MDButtonVariants.GRADIENT as unknown as MUIBaseVariants}
                  color={MUIColors.LIGHT as unknown as MUIBaseColors}
                  fullWidth
                  disabled={requestLoading}
                >
                  {requestLoading ? (
                    <FontAwesomeIcon icon={faCircleNotch} spin />
                  ) : (
                    "Send Reset Code"
                  )}
                </MDButton>
              </MDBox>

              <MDBox mt={3} mb={1} textAlign="center">
                <MDTypography variant="button" color={MUIColors.WHITE}>
                  <Link to='/authentication/sign-in' >
                    Sign In
                  </Link>
                </MDTypography>
              </MDBox>
            </MDBox>
          ) : (
            <MDBox component="form" role="form" onSubmit={submitNewPassword}>
              <MDBox mb={2}>
                <MDInput
                  type="text"
                  label="Verification Code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  fullWidth
                  required
                />
              </MDBox>

              <MDBox mb={2}>
                <MDInput
                  type="password"
                  label="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  fullWidth
                  required
                />
              </MDBox>

              <MDBox mb={2}>
                <MDInput
                  type="password"
                  label="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  fullWidth
                  required
                />
              </MDBox>

              <MDBox mt={4} mb={1}>
                <MDButton
                  variant={MDButtonVariants.GRADIENT as unknown as MUIBaseVariants}
                  color={MUIColors.LIGHT as unknown as MUIBaseColors}
                  fullWidth
                  type="submit"
                  disabled={requestLoading}
                >
                  {requestLoading ? (
                    <FontAwesomeIcon icon={faCircleNotch} spin />
                  ) : (
                    "Reset Password"
                  )}
                </MDButton>
              </MDBox>

              <MDBox mt={3} mb={1} textAlign="center">
                <MDTypography
                  color={MUIColors.WHITE}
                  onClick={() => setResetRequested(false)}
                  sx={{
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Resend Verification Code
                </MDTypography>
              </MDBox>
            </MDBox>
          )}
        </MDBox>
      </Card>
    </BasicLayout >
  );
};

export default ResetPasswordPage;
